/**
 * Agent engine routing + Codex model catalog (shared by api and web-vite).
 *
 * The sidecar keeps a verbatim copy of the routing helpers in
 * `optidev-dev-container-code-server/claude-agent/src/codex/codex-models.ts` (it cannot import this
 * package); both copies run the same test vectors.
 *
 * Model-key contract:
 *   - `codex/<openai-model>` → Codex engine (OpenAI models through the backend Responses proxy)
 *   - any other key with "/"  → OpenCode engine (OpenRouter ids)
 *   - everything else         → Claude engine
 */

export type AgentEngine = 'claude' | 'opencode' | 'codex';

/** Who pays for a turn. Phase 3 adds `chatgpt_plan` (Sign in with ChatGPT). */
export type BillingSource = 'platform_credits' | 'byoc_anthropic' | 'chatgpt_plan';

export const CODEX_MODEL_PREFIX = 'codex/';

export function isCodexModel(model: string | undefined | null): boolean {
  return typeof model === 'string' && model.startsWith(CODEX_MODEL_PREFIX);
}

export function getAgentEngine(model: string | undefined | null): AgentEngine {
  if (isCodexModel(model)) return 'codex';
  if (typeof model === 'string' && model.includes('/')) return 'opencode';
  return 'claude';
}

/** `codex/gpt-6.1-sol` → `gpt-6.1-sol` (the id OpenAI's Responses API expects). */
export function toOpenAiModelId(model: string): string {
  return isCodexModel(model) ? model.slice(CODEX_MODEL_PREFIX.length) : model;
}

export interface CodexModelDefinition {
  /** OpenAI model id sent to the Responses API. */
  openaiModel: string;
  label: string;
  /** Plan gating tier, same vocabulary as Claude models (`planFeatures.models`). */
  planTier: 'haiku' | 'sonnet' | 'opus';
  descriptor: string;
  tagline: string;
  description: string;
  bestFor: string[];
  intelligence: 1 | 2 | 3 | 4 | 5;
  speed: 1 | 2 | 3 | 4 | 5;
  costTier: 1 | 2 | 3 | 4 | 5;
}

/**
 * Curated catalog (Oct 2026). OpenAI retires models quickly; keep this list short and validate
 * against the live API before promoting an entry. Keys are ≤ 17 chars (agent_tasks.model is
 * varchar(50)).
 */
// Ordered premium → default → fast, mirroring the Anthropic group (Opus, Sonnet, Haiku).
export const CODEX_MODELS: Record<string, CodexModelDefinition> = {
  'codex/gpt-6-astra': {
    openaiModel: 'gpt-6-astra',
    label: 'GPT-6 Astra',
    planTier: 'opus',
    descriptor: 'Most capable',
    tagline: 'Frontier GPT for the most demanding work',
    description: 'OpenAI’s frontier model. Pick for hard multi-file refactors and deep reasoning; the most expensive GPT option.',
    bestFor: ['Complex refactors', 'Hard bugs', 'Deep reasoning'],
    intelligence: 5,
    speed: 2,
    costTier: 5,
  },
  'codex/gpt-6.1-sol': {
    openaiModel: 'gpt-6.1-sol',
    label: 'GPT-6.1 Sol',
    planTier: 'sonnet',
    descriptor: 'Default for GPT',
    tagline: 'Best all-round GPT — runs in OpenAI’s Codex harness',
    description:
      'OpenAI’s workhorse model for coding and everyday work, driven by the Codex agent harness (apply_patch, sandboxed shell).',
    bestFor: ['Coding', 'Refactors', 'Agentic builds'],
    intelligence: 4,
    speed: 3,
    costTier: 3,
  },
  'codex/gpt-6-luna': {
    openaiModel: 'gpt-6-luna',
    label: 'GPT-6 Luna',
    planTier: 'haiku',
    descriptor: 'Fast',
    tagline: 'Fast and affordable GPT for easier tasks',
    description: 'Fast, low-cost GPT model for simple edits, questions and quick iterations.',
    bestFor: ['Quick edits', 'Questions', 'Small tasks'],
    intelligence: 3,
    speed: 5,
    costTier: 1,
  },
};

export const CODEX_MODEL_KEYS = Object.keys(CODEX_MODELS) as Array<keyof typeof CODEX_MODELS & string>;

export const DEFAULT_CODEX_MODEL = 'codex/gpt-6.1-sol';

/**
 * Retired model keys → their replacement. GPT-5.6 Terra ran through OpenCode/OpenRouter with
 * placeholder pricing and no sandbox; OpenAI models now go through the Codex engine
 * (2026-10-30, team review of #90). Persisted selections (workspace default, project
 * selected-model, local state) are remapped at read time; no data migration needed.
 */
export const RETIRED_MODEL_REMAP: Record<string, string> = {
  'openai/gpt-5.6-terra': DEFAULT_CODEX_MODEL,
  'openrouter/openai/gpt-5.6-terra': DEFAULT_CODEX_MODEL,
};

/**
 * Resolve a stored/requested model key to one the caller may actually use:
 *   1. retired keys are remapped (see RETIRED_MODEL_REMAP);
 *   2. a Codex key on a viewer/environment where the Codex engine is NOT enabled falls back
 *      to `fallback` (the Claude default) instead of failing every turn with ENGINE_NOT_ENABLED.
 * Pure; shared by the api (workspace default, gateway) and web (sanitizeModel).
 */
export function resolveSelectableModel(
  model: string | null | undefined,
  opts: { codexEnabled: boolean; fallback: string },
): string {
  const remapped = model && RETIRED_MODEL_REMAP[model] ? RETIRED_MODEL_REMAP[model] : model;
  if (!remapped) return opts.fallback;
  if (isCodexModel(remapped) && !opts.codexEnabled) return opts.fallback;
  return remapped;
}

/** Error codes the gateway emits on `chat-error` for Codex turns (web handles them inline). */
export const CODEX_CHAT_ERROR_CODES = {
  ENGINE_NOT_ENABLED: 'ENGINE_NOT_ENABLED',
  ENGINE_UNAVAILABLE_RESTART_REQUIRED: 'ENGINE_UNAVAILABLE_RESTART_REQUIRED',
  MODEL_NOT_AVAILABLE: 'MODEL_NOT_AVAILABLE',
  CODEX_TURN_IN_PROGRESS: 'CODEX_TURN_IN_PROGRESS',
} as const;
export type CodexChatErrorCode = (typeof CODEX_CHAT_ERROR_CODES)[keyof typeof CODEX_CHAT_ERROR_CODES];
